/**
 * Warteschlange für Meldungen und Rückfragen — Ersatz für alert, confirm und prompt des
 * Browsers (Regel „Dokument-Navigation", Abschnitt Hinweisfenster). Ohne React: Seiten
 * rufen `dialogs.confirm(...)` und bekommen ein Promise; der DialogHost in der Shell zeigt
 * jeweils die vorderste Anfrage.
 *
 * Als Federation-Singleton geteilt — Portal und Module nutzen dieselbe Warteschlange.
 */
export type NotifyKind = 'info' | 'success' | 'error';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Rote Bestätigung, z. B. Löschen */
  danger?: boolean;
}

export interface NotifyOptions {
  title: string;
  message?: string;
  /** Aufzählung, z. B. fehlende Pflichtfelder */
  items?: string[];
  kind?: NotifyKind;
  closeLabel?: string;
}

export interface PromptOptions {
  title: string;
  message?: string;
  /** Beschriftung des Eingabefelds */
  fieldLabel?: string;
  initialValue?: string;
  placeholder?: string;
  multiline?: boolean;
  /** Leere Eingabe zulassen (Standard: nein) */
  allowEmpty?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  maxLength?: number;
}

export type DialogRequest =
  | { id: number; type: 'confirm'; options: ConfirmOptions; resolve: (ok: boolean) => void }
  | { id: number; type: 'notify'; options: NotifyOptions; resolve: () => void }
  | { id: number; type: 'prompt'; options: PromptOptions; resolve: (text: string | null) => void };

let queue: DialogRequest[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function push(req: DialogRequest): void {
  queue = [...queue, req];
  emit();
}

function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => push({ id: nextId++, type: 'confirm', options, resolve }));
}

function notifyDialog(options: NotifyOptions): Promise<void> {
  return new Promise((resolve) => push({ id: nextId++, type: 'notify', options, resolve }));
}

function errorDialog(title: string, err: unknown): Promise<void> {
  const message = err instanceof Error && err.message ? err.message : typeof err === 'string' && err ? err : 'Unbekannter Fehler';
  return notifyDialog({ title, message, kind: 'error' });
}

function promptDialog(options: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => push({ id: nextId++, type: 'prompt', options, resolve }));
}

export const dialogs = {
  /** Rückfrage; true = bestätigt */
  confirm: confirmDialog,
  /** Meldung mit einem Knopf */
  notify: notifyDialog,
  /** Fehlermeldung aus einem catch: zeigt die Meldung des Fehlers */
  error: errorDialog,
  /** Texteingabe; null = abgebrochen */
  promptText: promptDialog,
};

export type Dialogs = typeof dialogs;

/** Vorderste Anfrage; null = nichts anzuzeigen */
export function currentDialog(): DialogRequest | null {
  return queue[0] ?? null;
}

/** Schließt die Anfrage und liefert dem Aufrufer das Ergebnis. */
export function settleDialog(id: number, result: boolean | string | null): void {
  const req = queue.find((r) => r.id === id);
  if (!req) return;
  queue = queue.filter((r) => r.id !== id);
  if (req.type === 'confirm') req.resolve(result === true);
  else if (req.type === 'prompt') req.resolve(typeof result === 'string' ? result : null);
  else req.resolve();
  emit();
}

export function subscribeDialogs(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Nur für Tests. */
export function resetDialogsForTests(): void {
  queue = [];
  listeners.clear();
}
